'use client';

import React, { useEffect, useRef, useState } from "react";
import RoundedIcon from "@/components/ui/RoundedIcon/RoundedIcon";
import { ArrowUpRight, ClockCountdown, EnvelopeOpen, PhoneCall } from "@phosphor-icons/react";
import Headline from "@/components/ui/Headline/Headline";
import TextInputField from "@/components/ui/TextInputField/TextInputField";
import TextAreaField from "@/components/ui/TextAreaField/TextAreaField";
import { LanguageType, LanguageDirection } from "@/lib/types";
import { useDetectWrap } from "@/hooks/useDetectWrap";
import { SubmitFormButton } from "@/components/ui/SubmitFormButton";
import { useGSAP } from "@gsap/react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  createEmailChangeHandler,
  createPhoneChangeHandler,
  createRequiredFieldChangeHandler,
  createTextAreaChangeHandler,
  submitToZoho,
  checkAllMandatoryFields,
  checkFormValidationErrors,
  VALIDATION_HINTS,
  VALIDATION_HINTS_AR
} from "@/lib/formUtils";
import LinkButton from "@/components/ui/LinkButton/LinkButton";
import IconWrapper from "@/components/ui/IconWrapper/IconWrapper";
import { useIsLg } from '@/contexts/BreakpointContext';

gsap.registerPlugin(ScrollTrigger);


interface ContactSectionProps {
  language?: LanguageType
  heading: string
  subheading: string
  paragraph: string
  heading2: string
  paragraph2: string
  subheading3: string
  paragraph3: string
  subheading4: string
  paragraph4: string
  receptionLabel?: string
  receptionValue?: string
  supportLabel?: string
  supportValue?: string
  localTimeLabel?: string
  localTimeValue?: string
  buttonText?: string
  buttonText3: string
  buttonText4: string
  buttonTextSuccess?: string
  buttonTextError?: string
  disclaimerText?: string
  disclaimerTextSuccess?: string
  disclaimerTextError?: string
  resetButtonText?: string
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};


const ContactSection: React.FC<ContactSectionProps> = ({
  language = "english",
  heading,
  subheading,
  paragraph,
  heading2,
  paragraph2,
  subheading3,
  paragraph3,
  subheading4,
  paragraph4,
  buttonText3,
  buttonText4,
  receptionLabel = "Our front desk",
  receptionValue = "011 510 6699",
  supportLabel = "Reach us by mail",
  supportValue = "hello@rivive.sa",
  localTimeLabel = "Local time",
  localTimeValue = "XX:XX (GMT+3)",
  buttonText = "Send us your message",
  buttonTextSuccess = "Your message is with us!",
  buttonTextError = "Unable to send message",
  disclaimerText = "By submitting this form, you agree to our Privacy Policy. We treat your personal information with the same care we bring to every treatment.",
  disclaimerTextSuccess = "We're honored by your interest! One of our specialists will contact you shortly to answer your questions and guide your next steps.",
  disclaimerTextError = "Your message could not be sent at this moment. We invite you to try again shortly.",
  resetButtonText = "Reset the form",
}) => {
  const firstNameLabel = language === 'arabic' ? "الاسم الأول" : "First name";
  const firstNamePlaceholder = language === 'arabic' ? "أدخل اسمك الأول" : "Your first name";
  const lastNameLabel = language === 'arabic' ? "اسم العائلة" : "Last name";
  const lastNamePlaceholder = language === 'arabic' ? "أدخل اسم عائلتك" : "Your last name";
  const emailLabel = language === 'arabic' ? "البريد الإلكتروني" : "Email address";
  const emailPlaceholder = language === 'arabic' ? "عنوان بريدك الإلكتروني للتواصل" : "Your contact email";
  const phoneLabel = language === 'arabic' ? "رقم الهاتف" : "Phone number";
  const phonePlaceholder = language === 'arabic' ? "رقم هاتفك" : "Your phone number";
  const messageLabel = language === 'arabic' ? "رسالتك" : "Your message";
  const messagePlaceholder = language === 'arabic' ? "اكتب الرسالة التي تود مشاركتها معنا" : "Write the message you'd like to share with us";
  const isLg = useIsLg();
  const { ref, isWrapped } = useDetectWrap();
  const { ref: ref2, isWrapped: isWrapped2 } = useDetectWrap();
  const heading2Ref = useRef<HTMLDivElement>(null);
  const receptionRef = useRef<HTMLAnchorElement>(null);
  const supportRef = useRef<HTMLAnchorElement>(null);
  const localTimeRef = useRef<HTMLDivElement>(null);

  const validationHints = language === 'arabic' ? VALIDATION_HINTS_AR : VALIDATION_HINTS;

  const [formData, setFormData] = useState({
    First_Name: "",
    Last_Name: "",
    Email: "",
    Phone_number: "",
    Personal_Notes: ""
  });

  const [isFirstNameInvalid, setIsFirstNameInvalid] = useState(false);
  const [isLastNameInvalid, setIsLastNameInvalid] = useState(false);
  const [isEmailInvalid, setIsEmailInvalid] = useState(false);
  const [isPhoneInvalid, setIsPhoneInvalid] = useState(false);
  const [isMessageInvalid, setIsMessageInvalid] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');

  const [currentTimeGMT3, setCurrentTimeGMT3] = useState<string>('');

  useEffect(() => {
    const updateTime = () => {
      const now = new Date();
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Asia/Riyadh',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
      const timeString = formatter.format(now);
      setCurrentTimeGMT3(`${timeString} (GMT+3)`);
    };

    updateTime();

    const interval = setInterval(updateTime, 10000);

    return () => clearInterval(interval);
  }, []);

  const handleFirstNameChange = createRequiredFieldChangeHandler(setFormData, setIsFirstNameInvalid, 'First_Name');
  const handleLastNameChange = createRequiredFieldChangeHandler(setFormData, setIsLastNameInvalid, 'Last_Name');
  const handleEmailChange = createEmailChangeHandler(setFormData, setIsEmailInvalid, 'Email');
  const handlePhoneChange = createPhoneChangeHandler(setFormData, setIsPhoneInvalid, 'Phone_number');
  const handleMessageChange = createTextAreaChangeHandler(setFormData, setIsMessageInvalid, 'Personal_Notes', formData.Personal_Notes);

  const hasAllMandatoryFields = checkAllMandatoryFields(formData, ['First_Name', 'Last_Name', 'Email', 'Phone_number', 'Personal_Notes']);

  const hasValidationErrors = checkFormValidationErrors(
    {
      firstName: isFirstNameInvalid,
      lastName: isLastNameInvalid,
      email: isEmailInvalid,
      phone: isPhoneInvalid,
      message: isMessageInvalid
    },
    hasAllMandatoryFields
  );

  const handleSubmit = async () => {
    if (hasValidationErrors) return;

    setSubmitStatus('submitting');
    const result = await submitToZoho(formData, 'Website - Contact');

    if (result.success) {
      setSubmitStatus('success');
    } else {
      setSubmitStatus('error');
    }
  };

  useEffect(() => {
    if (submitStatus !== 'idle' && submitStatus !== 'submitting') {
      setSubmitStatus('idle');
    }
  }, [formData]);

  useGSAP(() => {
    const reception = receptionRef.current;
    const support = supportRef.current;
    const localTime = localTimeRef.current;

    if (reception && support && localTime) {

      const elements = [reception, support, localTime];

      gsap.set(elements, {
        autoAlpha: 0,
        filter: 'blur(30px)',
        y: +100,
        force3D: true,
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: reception,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      });

      tl.to(elements, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 1.0,
        stagger: 0.2,
        ease: "power2.out",
        force3D: true,
      });
    }

    const heading2El = heading2Ref.current;
    const ref2El = ref2.current;

    if (heading2El && ref2El) {

      const elements2 = [heading2El, ref2El];

      gsap.set(elements2, {
        autoAlpha: 0,
        filter: 'blur(30px)',
        y: +100,
        force3D: true,
      });

      const tl2 = gsap.timeline({
        scrollTrigger: {
          trigger: heading2El,
          start: "top 80%",
          toggleActions: "play none none none"
        }
      });

      tl2.to(elements2, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        y: 0,
        duration: 1.0,
        stagger: 0.2,
        ease: "power2.out",
        force3D: true,
      });

      tl2.to(ref2El, {
        paddingTop: isLg && !isWrapped2 ? '8rem' : 0
      }, "<");
    }

  }, { dependencies: [isLg], revertOnUpdate: true });

  return (
    <section
      style={{ direction: LANGUAGE_TO_DIRECTION[language] }}
      className="
        w-full
        px-[1.25rem]
        pt-[2rem]
        padding-main-x-desktop
        padding-main-t-desktop
        flex flex-col gap-y-[4.5rem] lg:gap-y-[7rem]
        items-center justify-center
      ">
      <div className="w-full container-medium">
        <div className="flex flex-col lg:flex-row lg:flex-wrap gap-y-[4.5rem] lg:gap-x-[4rem] lg:gap-y-[4.5rem]">
          <div className="w-full lg:w-[30.6875rem] lg:flex-grow lg:max-w-[49.6875rem] flex flex-col gap-[2rem] lg:gap-[4.5rem]">
            <div className="flex flex-row">
              <Headline
                type="short"
                heading={heading}
                subheading={subheading}
                paragraph={paragraph}
                language={language}
                alignment={language === "arabic" ? "right" : "left"}
              />
            </div>
            <div className="flex flex-col gap-[1.5rem] lg:gap-[2rem]">
              <a
                ref={receptionRef}
                href={`tel:${receptionValue}`}
                className="lg:min-w-[24.3125rem] flex flex-row gap-[1rem] lg:gap-[1.5rem] items-center justify-start cursor-pointer"
              >
                <RoundedIcon
                  className="rounded-full"
                  size={isLg ? 64 : 44}
                >
                  <PhoneCall className="text-graphics-primary" />
                </RoundedIcon>
                <div className="flex flex-col gap-[0.5rem]">
                  <p className="text-16--alexandria text-text-quaternary" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                    {receptionLabel}
                  </p>
                  <p className="text-text-primary text-28--neue-june" style={{ direction: "ltr" }}>
                    {receptionValue}
                  </p>
                </div>
              </a>

              <a
                ref={supportRef}
                href={`mailto:${supportValue}`}
                className="lg:min-w-[24.3125rem] flex flex-row gap-[1rem] lg:gap-[1.5rem] items-center justify-start cursor-pointer"
              >
                <RoundedIcon
                  className="rounded-full"
                  size={isLg ? 64 : 44}
                >
                  <EnvelopeOpen className="text-graphics-primary" />
                </RoundedIcon>
                <div className="flex flex-col gap-[0.5rem]">
                  <p className="text-16--alexandria text-text-quaternary" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                    {supportLabel}
                  </p>
                  <p className="text-text-primary text-28--neue-june" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                    {supportValue}
                  </p>
                </div>
              </a>
              <div ref={localTimeRef} className="lg:min-w-[24.3125rem] flex flex-row gap-[1rem] lg:gap-[1.5rem] items-center justify-start">
                <RoundedIcon
                  className="rounded-full"
                  size={isLg ? 64 : 44}
                >
                  <ClockCountdown className="text-graphics-primary" />
                </RoundedIcon>
                <div className="flex flex-col gap-[0.5rem]">
                  <p className="text-16--alexandria text-text-quaternary" style={{ direction: LANGUAGE_TO_DIRECTION[language] }}>
                    {localTimeLabel}
                  </p>
                  <p className="text-text-primary text-28--neue-june" style={{ direction: "ltr" }}>
                    {currentTimeGMT3 || localTimeValue}
                  </p>
                </div>
              </div>
            </div>
          </div>


          <div
            ref={ref}
            className={`w-full flex flex-col lg:w-[30.6875rem] lg:flex-grow lg:max-w-[43.3125rem] gap-y-[0.75rem]`}
            style={{
              maxWidth: isLg && !isWrapped ? '43.3125rem' : '100%',
              paddingTop: isLg && !isWrapped ? '14.625rem' : 0
            }}
          >
            <div className="flex flex-col gap-y-[0.25rem] lg:gap-y-[0.75rem]">
              <div className="flex flex-col tb:flex-row tb:flex-wrap gap-x-[1rem] gap-y-[0.25rem] lg:gap-y-[0.75rem] flex-grow ">
                <div style={{ direction: "ltr" }} className="flex-grow">
                  <TextInputField
                    size={isLg ? 20 : 18}
                    language={language}
                    label={firstNameLabel}
                    placeholder={firstNamePlaceholder}
                    value={formData.First_Name}
                    onChange={handleFirstNameChange}
                    invalid={isFirstNameInvalid}
                    hint={isFirstNameInvalid ? validationHints.FIRST_NAME_REQUIRED : undefined}
                  />
                </div>

                <div style={{ direction: "ltr" }} className="flex-grow">
                  <TextInputField
                    size={isLg ? 20 : 18}
                    language={language}
                    label={lastNameLabel}
                    placeholder={lastNamePlaceholder}
                    value={formData.Last_Name}
                    onChange={handleLastNameChange}
                    invalid={isLastNameInvalid}
                    hint={isLastNameInvalid ? validationHints.LAST_NAME_REQUIRED : undefined}
                  />
                </div>
              </div>

              <div className="flex flex-col tb:flex-row tb:flex-wrap gap-x-[1rem] gap-y-[0.25rem] lg:gap-y-[0.75rem] flex-grow ">
                <div style={{ direction: "ltr" }} className="flex-grow">
                  <TextInputField
                    size={isLg ? 20 : 18}
                    language={language}
                    label={emailLabel}
                    placeholder={emailPlaceholder}
                    value={formData.Email}
                    onChange={handleEmailChange}
                    invalid={isEmailInvalid}
                    hint={isEmailInvalid ? validationHints.EMAIL_INVALID : undefined}
                  />
                </div>

                <div style={{ direction: "ltr" }} className="flex-grow">
                  <TextInputField
                    language={language}
                    label={phoneLabel}
                    placeholder={phonePlaceholder}
                    size={isLg ? 20 : 18}
                    value={formData.Phone_number}
                    onChange={handlePhoneChange}
                    invalid={isPhoneInvalid}
                    hint={isPhoneInvalid ? validationHints.PHONE_INVALID : undefined}
                  />
                </div>
              </div>
              <div style={{ direction: "ltr" }} className="flex-grow">
                <TextAreaField
                  size={isLg ? 20 : 18}
                  language={language}
                  label={messageLabel}
                  placeholder={messagePlaceholder}
                  value={formData.Personal_Notes}
                  onChange={handleMessageChange}
                  invalid={isMessageInvalid}
                  hint={isMessageInvalid ? validationHints.CHARACTER_LIMIT_REACHED : undefined}
                />
              </div>
            </div>
            <SubmitFormButton
              isLg={isLg}
              onClick={handleSubmit}
              onReset={() => {
                setFormData({
                  First_Name: "",
                  Last_Name: "",
                  Email: "",
                  Phone_number: "",
                  Personal_Notes: ""
                });
                setSubmitStatus('idle');
              }}
              disabled={hasValidationErrors || submitStatus === 'submitting'}
              language={language}
              buttonText={
                submitStatus === 'success'
                  ? buttonTextSuccess
                  : submitStatus === 'error'
                    ? buttonTextError
                    : buttonText
              }
              resetButtonText={resetButtonText}
              privacyText={
                submitStatus === 'success'
                  ? disclaimerTextSuccess
                  : submitStatus === 'error'
                    ? disclaimerTextError
                    : disclaimerText
              }
              wrapperText={
                submitStatus === 'success'
                  ? disclaimerTextSuccess
                  : submitStatus === 'error'
                    ? disclaimerTextError
                    : ''
              }
              wrapperBgColor={
                submitStatus === 'success'
                  ? 'bg-greens-green-2'
                  : submitStatus === 'error'
                    ? 'bg-state-error-base'
                    : 'bg-greens-green-2'
              }
            />
          </div>
        </div>
      </div>





      <div className="

        w-full container-large
        flex items-center justify-center
        border-t-main border-stroke-soft-200
        pt-[4.5rem] lg:pt-[7rem]">
        <div className="w-full container-medium flex flex-col lg:flex-row lg:gap-x-[4rem] lg:justify-between lg:flex-wrap gap-y-[2.5rem] lg:gap-y-[4.5rem]">

          <div ref={heading2Ref} className="flex flex-col gap-[1.5rem] w-full lg:w-[30.6875rem] lg:flex-grow tb:max-w-[37rem]">
            <h3 className="text-text-primary text-36-to-24--neue-june whitespace-pre-line">
              {heading2}
            </h3>
            <p className="text-text-tertiary text-18--alexandria">
              {paragraph2}
            </p>
          </div>

          <div
            ref={ref2}
            className={`flex flex-col gap-[2.5rem] lg:gap-[3.5rem] w-full lg:w-[30.6875rem] lg:flex-grow tb:max-w-[43.3125rem] lg:pt-[8rem]`}
            style={{
              paddingTop: isLg && !isWrapped2 ? '8rem' : 0
            }}
          >

            <div className="flex flex-col gap-[1.5rem] lg:gap-[2rem] ">
              <h4 className="text-text-primary text-28--neue-june">
                {subheading3}
              </h4>
              <p className="text-text-tertiary text-18--alexandria" style={{ whiteSpace: 'pre-line' }}>
                {paragraph3}
              </p>
              <div className="flex flex-row gap-[0.25rem] lg:gap-[0.5rem] items-center justify-start">
                <LinkButton
                  text={buttonText3}
                  onClick={() => window.open('mailto:business@rivive.sa', '_self')}
                  size={isLg ? 18 : 16}
                  language={language}
                />
                <IconWrapper
                  size={20}
                >
                  <ArrowUpRight className="text-graphics-quaternary" />
                </IconWrapper>
              </div>
            </div>


            <div className=" flex flex-col gap-[1.5rem] lg:gap-[2rem] ">

              <h4 className="text-text-primary text-28--neue-june">
                {subheading4}
              </h4>

              <p className="text-text-tertiary text-18--alexandria" style={{ whiteSpace: 'pre-line' }}>
                {paragraph4}
              </p>

              <div className="flex flex-row gap-[0.25rem] lg:gap-[0.5rem] items-center justify-start">

                <LinkButton
                  text={buttonText4}
                  onClick={() => window.open('mailto:business@rivive.sa', '_self')}
                  size={isLg ? 18 : 16}
                  language={language}
                />
                <IconWrapper
                  size={20}
                >
                  <ArrowUpRight className="text-graphics-quaternary" />
                </IconWrapper>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


export default ContactSection;
